import { useEffect, useState, useCallback } from "react";
import AdminLayout from "../components/AdminLayout";
import LoadingButton from "../components/LoadingButton";
import { Plus, Search, Eye, X, Zap, RefreshCcw, Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, formatRupiah, formatDate } from "../lib/api";

const TABS = [
  { key: "list", label: "Daftar Pinjaman" },
  { key: "rekap", label: "Rekap per Unit" },
  { key: "report", label: "Laporan Bulanan" },
];

const STATUS_PRESETS = [
  { key: "", label: "Semua" },
  { key: "pending", label: "Menunggu" },
  { key: "active", label: "Aktif" },
  { key: "completed", label: "Lunas" },
  { key: "rejected", label: "Ditolak" },
];

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

const EMPTY_FORM = { member_id: "", loan_type_id: "", amount: "", tenor: "", purpose: "" };

function ketBadge(ket) {
  const label = ket || "-";
  if (label === "LUNAS") return "bg-emerald-100 text-emerald-700";
  if (label === "CICILAN BERJALAN") return "bg-blue-100 text-blue-700";
  if (label === "BELUM LUNAS") return "bg-amber-100 text-amber-700";
  return "bg-slate-100 text-slate-600";
}

export default function AdminManagementUtang() {
  const [tab, setTab] = useState("list");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const [master, setMaster] = useState(null);
  const [rows, setRows] = useState([]);
  const [workUnits, setWorkUnits] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  // filters
  const [q, setQ] = useState("");
  const [unit, setUnit] = useState("");
  const [status, setStatus] = useState("");

  // input modal
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // detail modal
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [earlyOpen, setEarlyOpen] = useState(false);
  const [earlyForm, setEarlyForm] = useState({ type: "total", count: "", payment_date: new Date().toISOString().slice(0, 10) });
  const [earlySubmitting, setEarlySubmitting] = useState(false);

  // rekap
  const [rekap, setRekap] = useState([]);
  const [rekapLoading, setRekapLoading] = useState(false);
  const [rekapLoaded, setRekapLoaded] = useState(false);

  // laporan
  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [m, p, w, mem] = await Promise.all([
        api.get("/admin/master-data"),
        api.get("/admin/pinjaman"),
        api.get("/admin/pinjaman/work-units"),
        api.get("/admin/members", { params: { status: "active" } }),
      ]);
      setMaster(m.data);
      setRows(Array.isArray(p.data) ? p.data : (p.data?.data || []));
      setWorkUnits(Array.isArray(w.data) ? w.data : []);
      setMembers(Array.isArray(mem.data) ? mem.data : []);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/pinjaman/${id}`);
      setDetail(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setDetailLoading(false); }
  }, []);

  const openDetail = (r) => {
    setDetail({ id: r.id });
    setEarlyOpen(false);
    setEarlyForm({ type: "total", count: "", payment_date: new Date().toISOString().slice(0, 10) });
    loadDetail(r.id);
  };

  const submitPinjaman = async (e) => {
    e.preventDefault();
    setFormError("");
    setSubmitting(true);
    try {
      await api.post("/admin/pinjaman", {
        member_id: form.member_id,
        loan_type_id: form.loan_type_id,
        amount: parseFloat(form.amount) || 0,
        tenor: parseInt(form.tenor, 10) || 0,
        purpose: form.purpose,
      });
      toast.success("Pinjaman berhasil dicatat");
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) { setFormError(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const payInstallment = async (inst, pinjamanId) => {
    setBusy(`pay-${inst.id}`);
    try {
      await api.post(`/admin/installments/${inst.id}/pay`, { payment_date: new Date().toISOString().slice(0, 10) });
      toast.success("Angsuran berhasil dibayar");
      await loadDetail(pinjamanId);
      load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const syncInstallment = async (pinjamanId) => {
    const insts = detail?.installments || [];
    const target = insts.find((i) => i.status === "belum lunas") || insts[0];
    if (!target) return;
    if (!window.confirm(`Sinkronkan status angsuran ke-${target.installment_number}?`)) return;
    setBusy("sync");
    try {
      const { data } = await api.post("/admin/installments/sync", { installment_id: target.id });
      toast.success(data?.message || "Sinkronisasi berhasil");
      await loadDetail(pinjamanId);
      load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const submitEarlySettle = async (e) => {
    e.preventDefault();
    setEarlySubmitting(true);
    try {
      const payload = { type: earlyForm.type, payment_date: earlyForm.payment_date };
      if (earlyForm.type === "partial") payload.count = parseInt(earlyForm.count, 10) || 0;
      const { data } = await api.post(`/admin/pinjaman/${detail.id}/early-settle`, payload);
      toast.success(data?.message || "Pelunasan dipercepat diproses");
      if (data) {
        toast.message(`Sisa pokok: ${formatRupiah(data.sisa_pokok ?? 0)} · Denda: ${formatRupiah(data.denda ?? 0)} · Total bayar: ${formatRupiah(data.total_bayar ?? 0)}`);
      }
      setEarlyOpen(false);
      await loadDetail(detail.id);
      load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setEarlySubmitting(false); }
  };

  const loadRekap = async () => {
    setRekapLoading(true);
    try {
      const { data } = await api.get("/admin/pinjaman/rekap-per-unit");
      setRekap(Array.isArray(data) ? data : []);
      setRekapLoaded(true);
    } catch (e) { setError(formatApiError(e)); }
    finally { setRekapLoading(false); }
  };

  const loadReport = async () => {
    setReportLoading(true);
    try {
      const { data } = await api.get("/admin/pinjaman/report", { params: { year: parseInt(year, 10), month: parseInt(month, 10) } });
      setReport(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setReportLoading(false); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/pinjaman/report/export", {
        params: { year: parseInt(year, 10), month: parseInt(month, 10) },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Laporan_USP_${month}_${year}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Laporan berhasil diekspor");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExporting(false); }
  };

  const filtered = rows.filter((r) => {
    const term = q.trim().toLowerCase();
    const okQ = !term || String(r.member_name || "").toLowerCase().includes(term) || String(r.account_no || "").toLowerCase().includes(term);
    const okUnit = !unit || r.work_unit === unit;
    const okStatus = !status || r.status === status;
    return okQ && okUnit && okStatus;
  });

  const reportRows = report?.data || [];
  const reportTotal = report?.total || {};
  const firstUnpaid = (detail?.installments || []).find((i) => i.status === "belum lunas");

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight text-brand-ink">Management Utang</h1>
            <p className="text-brand-muted text-sm mt-1">Input pinjaman, jadwal angsuran, dan laporan bulanan.</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => (
            <FilterChip key={t.key} active={tab === t.key} onClick={() => setTab(t.key)} testid={`utang-tab-${t.key}`}>
              {t.label}
            </FilterChip>
          ))}
        </div>

        {error && <div className="rounded-2xl bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3" data-testid="utang-error">{error}</div>}

        {tab === "list" && (
          <div className="space-y-4">
            <div className="bg-white rounded-3xl border border-brand/10 shadow-sm p-4 flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <FormField label="Cari">
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
                    <input className="inp pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nama anggota atau no. rekening" data-testid="utang-search-input" />
                  </div>
                </FormField>
              </div>
              <div className="w-48">
                <FormField label="Unit Kerja">
                  <select className="inp" value={unit} onChange={(e) => setUnit(e.target.value)} data-testid="utang-unit-select">
                    <option value="">Semua Unit</option>
                    {workUnits.map((w) => <option key={w} value={w}>{w}</option>)}
                  </select>
                </FormField>
              </div>
              <div className="w-44">
                <FormField label="Status">
                  <select className="inp" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="utang-status-select">
                    {STATUS_PRESETS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </FormField>
              </div>
              <button onClick={() => setOpen(true)} data-testid="open-pinjaman-utang-btn" className="rounded-full bg-brand text-white px-5 py-2.5 text-sm flex items-center gap-2 hover:bg-brand-hover transition">
                <Plus size={14} /> Input Pinjaman
              </button>
              <style>{`.inp{width:100%;border-radius:12px;border:1px solid rgba(11,110,110,0.2);padding:9px 12px;font-size:14px;background:white;outline:none}.inp:focus{box-shadow:0 0 0 3px rgba(11,110,110,0.15);border-color:rgba(11,110,110,0.4)}`}</style>
            </div>

            <div className="bg-white rounded-3xl border border-brand/10 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm whitespace-nowrap" data-testid="utang-table">
                  <thead className="bg-brand-soft/60 text-left text-brand-muted">
                    <tr>
                      <th className="px-4 py-3">No</th>
                      <th className="px-4 py-3">Nama</th>
                      <th className="px-4 py-3">Unit</th>
                      <th className="px-4 py-3">Jenis</th>
                      <th className="px-4 py-3 text-right">Jumlah</th>
                      <th className="px-4 py-3 text-right">Tenor</th>
                      <th className="px-4 py-3 text-right">Jasa %</th>
                      <th className="px-4 py-3 text-right">Angs Pokok</th>
                      <th className="px-4 py-3 text-right">Jasa KPRI</th>
                      <th className="px-4 py-3 text-right">Jml Angsuran</th>
                      <th className="px-4 py-3 text-center">Angs Ke</th>
                      <th className="px-4 py-3 text-right">Sisa</th>
                      <th className="px-4 py-3">Ket</th>
                      <th className="px-4 py-3 text-right">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && rows.length === 0 ? <tr><td colSpan={14} className="px-4 py-10 text-center text-brand-muted">Memuat…</td></tr>
                    : filtered.length === 0 ? <tr><td colSpan={14} className="px-4 py-10 text-center text-brand-muted">Belum ada pinjaman</td></tr>
                    : filtered.map((r, idx) => (
                      <tr key={r.id} className="border-t border-brand/10 hover:bg-brand-soft/30">
                        <td className="px-4 py-3">{idx + 1}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-brand-ink">{r.member_name || "-"}</div>
                          {r.account_no && <div className="text-xs text-brand-muted">{r.account_no}</div>}
                        </td>
                        <td className="px-4 py-3">{r.work_unit || "-"}</td>
                        <td className="px-4 py-3">{r.loan_type_name || "-"}</td>
                        <td className="px-4 py-3 text-right">{formatRupiah(r.amount)}</td>
                        <td className="px-4 py-3 text-right">{r.tenor_months ?? 0}</td>
                        <td className="px-4 py-3 text-right">{r.interest_rate ?? 0}</td>
                        <td className="px-4 py-3 text-right">{formatRupiah(r.angs_pokok)}</td>
                        <td className="px-4 py-3 text-right">{formatRupiah(r.jasa_kpri)}</td>
                        <td className="px-4 py-3 text-right">{formatRupiah(r.jumlah_angsuran)}</td>
                        <td className="px-4 py-3 text-center">{r.paid_count ?? 0} / {r.total_count ?? 0}</td>
                        <td className="px-4 py-3 text-right font-semibold text-brand-ink">{formatRupiah(r.sisa)}</td>
                        <td className="px-4 py-3">
                          <span className={"inline-flex items-center rounded-full text-xs px-2.5 py-1 " + ketBadge(r.status_label)}>
                            {r.status_label || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => openDetail(r)} data-testid={`detail-pinjaman-${r.id}`} className="rounded-full bg-brand-soft text-brand px-3 py-1.5 text-xs inline-flex items-center gap-1 hover:bg-brand/15 transition">
                            <Eye size={12} /> Detail
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "rekap" && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <LoadingButton onClick={loadRekap} loading={rekapLoading} loadingText="Memuat…" data-testid="rekap-load-btn" className="rounded-full bg-brand text-white px-5 py-2.5 text-sm hover:bg-brand-hover">
                <RefreshCcw size={14} /> Muat
              </LoadingButton>
            </div>
            <div className="bg-white rounded-3xl border border-brand/10 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="rekap-table">
                  <thead className="bg-brand-soft/60 text-left text-brand-muted">
                    <tr>
                      <th className="px-4 py-3">Unit</th>
                      <th className="px-4 py-3 text-right">Jumlah Pinjaman</th>
                      <th className="px-4 py-3 text-right">Total Pinjaman</th>
                      <th className="px-4 py-3 text-right">Total Sisa</th>
                      <th className="px-4 py-3 text-right">Lunas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rekap.length === 0 ? <tr><td colSpan={5} className="px-4 py-10 text-center text-brand-muted">{rekapLoaded ? "Belum ada data rekap" : "Klik Muat untuk menampilkan rekap"}</td></tr>
                    : rekap.map((r, idx) => (
                      <tr key={r.unit || idx} className="border-t border-brand/10 hover:bg-brand-soft/30">
                        <td className="px-4 py-3 font-medium text-brand-ink">{r.unit || "-"}</td>
                        <td className="px-4 py-3 text-right">{r.total_loans ?? 0}</td>
                        <td className="px-4 py-3 text-right">{formatRupiah(r.total_amount)}</td>
                        <td className="px-4 py-3 text-right">{formatRupiah(r.total_sisa)}</td>
                        <td className="px-4 py-3 text-right">{r.lunas_count ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "report" && (
          <div className="space-y-4">
            <div className="bg-white rounded-3xl border border-brand/10 shadow-sm p-4 flex flex-wrap items-end gap-3">
              <div className="w-36">
                <FormField label="Tahun">
                  <select className="inp" value={year} onChange={(e) => setYear(e.target.value)} data-testid="report-year-select">
                    {Array.from({ length: 6 }, (_, i) => now.getFullYear() - i).map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </FormField>
              </div>
              <div className="w-44">
                <FormField label="Bulan">
                  <select className="inp" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="report-month-select">
                    {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                </FormField>
              </div>
              <LoadingButton onClick={loadReport} loading={reportLoading} loadingText="Memuat…" data-testid="report-show-btn" className="rounded-full bg-brand text-white px-5 py-2.5 text-sm hover:bg-brand-hover">
                <FileSpreadsheet size={14} /> Tampilkan
              </LoadingButton>
              <LoadingButton onClick={exportCsv} loading={exporting} loadingText="Mengekspor…" data-testid="report-export-btn" className="rounded-full bg-brand-soft text-brand px-5 py-2.5 text-sm hover:bg-brand/15">
                <Download size={14} /> Export CSV
              </LoadingButton>
              <style>{`.inp{width:100%;border-radius:12px;border:1px solid rgba(11,110,110,0.2);padding:9px 12px;font-size:14px;background:white;outline:none}.inp:focus{box-shadow:0 0 0 3px rgba(11,110,110,0.15);border-color:rgba(11,110,110,0.4)}`}</style>
            </div>

            <div className="bg-white rounded-3xl border border-brand/10 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm whitespace-nowrap" data-testid="report-table">
                  <thead className="bg-brand-soft/60 text-left text-brand-muted">
                    <tr>
                      <th className="px-4 py-3">No</th>
                      <th className="px-4 py-3">Nama</th>
                      <th className="px-4 py-3">Unit</th>
                      <th className="px-4 py-3 text-right">Jumlah Pinjaman</th>
                      <th className="px-4 py-3 text-right">Jasa %</th>
                      <th className="px-4 py-3 text-right">Jasa Koperasi</th>
                      <th className="px-4 py-3 text-center">Angs Ke</th>
                      <th className="px-4 py-3 text-right">Jml Angs</th>
                      <th className="px-4 py-3 text-right">Angs Pokok</th>
                      <th className="px-4 py-3 text-right">Jasa KPRI</th>
                      <th className="px-4 py-3 text-right">Jml Angsuran</th>
                      <th className="px-4 py-3 text-right">Sisa Pinjaman</th>
                      <th className="px-4 py-3">Ket</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportLoading ? <tr><td colSpan={13} className="px-4 py-10 text-center text-brand-muted">Memuat…</td></tr>
                    : !report ? <tr><td colSpan={13} className="px-4 py-10 text-center text-brand-muted">Pilih periode lalu klik Tampilkan</td></tr>
                    : reportRows.length === 0 ? <tr><td colSpan={13} className="px-4 py-10 text-center text-brand-muted">Tidak ada data laporan</td></tr>
                    : reportRows.map((r, idx) => (
                      <tr key={r.id ?? idx} className="border-t border-brand/10 hover:bg-brand-soft/30">
                        <td className="px-4 py-3">{idx + 1}</td>
                        <td className="px-4 py-3 font-medium text-brand-ink">{r.nama || "-"}</td>
                        <td className="px-4 py-3">{r.unit || "-"}</td>
                        <td className="px-4 py-3 text-right">{formatRupiah(r.jumlah_pinjaman)}</td>
                        <td className="px-4 py-3 text-right">{r.jasa_persen ?? 0}</td>
                        <td className="px-4 py-3 text-right">{formatRupiah(r.jasa_koperasi)}</td>
                        <td className="px-4 py-3 text-center">{r.angs_ke ?? 0}</td>
                        <td className="px-4 py-3 text-right">{r.jml_angs ?? 0}</td>
                        <td className="px-4 py-3 text-right">{formatRupiah(r.angs_pokok)}</td>
                        <td className="px-4 py-3 text-right">{formatRupiah(r.jasa_kpri)}</td>
                        <td className="px-4 py-3 text-right">{formatRupiah(r.jumlah_angsuran)}</td>
                        <td className="px-4 py-3 text-right">{formatRupiah(r.sisa_pinjaman)}</td>
                        <td className="px-4 py-3">{r.ket || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                  {report && reportRows.length > 0 && (
                    <tfoot className="bg-brand-soft/60 font-semibold text-brand-ink">
                      <tr className="border-t border-brand/10">
                        <td className="px-4 py-3" colSpan={3}>Total</td>
                        <td className="px-4 py-3 text-right">{formatRupiah(reportTotal.jumlah_pinjaman)}</td>
                        <td className="px-4 py-3 text-right">-</td>
                        <td className="px-4 py-3 text-right">-</td>
                        <td className="px-4 py-3 text-center">-</td>
                        <td className="px-4 py-3 text-right">-</td>
                        <td className="px-4 py-3 text-right">-</td>
                        <td className="px-4 py-3 text-right">-</td>
                        <td className="px-4 py-3 text-right">{formatRupiah(reportTotal.jumlah_angsuran)}</td>
                        <td className="px-4 py-3 text-right">{formatRupiah(reportTotal.sisa_pinjaman)}</td>
                        <td className="px-4 py-3">-</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input Pinjaman modal */}
      {open && (
        <div className="fixed inset-0 z-50 bg-brand-ink/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={() => setOpen(false)}>
          <form onSubmit={submitPinjaman} className="bg-white rounded-3xl w-full sm:max-w-md p-5 sm:p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="utang-pinjaman-form">
            <div className="font-heading font-bold text-lg text-brand-ink">Input Pinjaman</div>
            <FormField label="Anggota">
              <select required className="inp" value={form.member_id} onChange={(e) => setForm((p) => ({ ...p, member_id: e.target.value }))} data-testid="utang-member-select">
                <option value="">— Pilih anggota —</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>{`${m.member_number ? `[${m.member_number}] ` : ""}${m.full_name}`}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Jenis Pinjaman">
              <select required className="inp" value={form.loan_type_id} onChange={(e) => setForm((p) => ({ ...p, loan_type_id: e.target.value }))} data-testid="utang-loan-type-select">
                <option value="">— Pilih jenis —</option>
                {(master?.loan_types || []).map((lt) => <option key={lt.id} value={lt.id}>{lt.name}</option>)}
              </select>
            </FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Jumlah (Rp)">
                <input type="number" min="0" required className="inp" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} data-testid="utang-amount-input" />
              </FormField>
              <FormField label="Tenor (bln)">
                <input type="number" min="1" required className="inp" value={form.tenor} onChange={(e) => setForm((p) => ({ ...p, tenor: e.target.value }))} data-testid="utang-tenor-input" />
              </FormField>
            </div>
            <FormField label="Keperluan">
              <input className="inp" value={form.purpose} onChange={(e) => setForm((p) => ({ ...p, purpose: e.target.value }))} data-testid="utang-purpose-input" />
            </FormField>

            {formError && <div className="rounded-xl bg-red-50 border border-red-200 text-red-600 text-xs px-3 py-2">{formError}</div>}

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setOpen(false)} disabled={submitting} className="rounded-full border border-brand/20 px-4 py-2 text-sm disabled:opacity-60">Batal</button>
              <LoadingButton type="submit" loading={submitting} loadingText="Menyimpan…" data-testid="utang-pinjaman-submit-btn" className="rounded-full bg-brand text-white px-5 py-2 text-sm hover:bg-brand-hover">Simpan</LoadingButton>
            </div>
            <style>{`.inp{width:100%;border-radius:12px;border:1px solid rgba(11,110,110,0.2);padding:9px 12px;font-size:14px;background:white;outline:none}.inp:focus{box-shadow:0 0 0 3px rgba(11,110,110,0.15);border-color:rgba(11,110,110,0.4)}`}</style>
          </form>
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-brand-ink/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-3xl w-full sm:max-w-4xl max-h-[92vh] overflow-y-auto p-5 sm:p-6 space-y-5" onClick={(e) => e.stopPropagation()} data-testid="utang-detail-modal">
            <div className="flex items-start justify-between gap-3">
              <div className="font-heading font-bold text-lg text-brand-ink">Detail Pinjaman</div>
              <button onClick={() => setDetail(null)} data-testid="utang-detail-close-btn" className="p-1.5 rounded-full bg-brand-soft text-brand hover:bg-brand/15 transition"><X size={16} /></button>
            </div>

            {detailLoading && !detail.member_name ? (
              <div className="py-10 text-center text-brand-muted text-sm">Memuat…</div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Info label="Nama" value={detail.member_name} />
                  <Info label="Unit" value={detail.work_unit} />
                  <Info label="Jenis Pinjaman" value={detail.loan_type_name} />
                  <Info label="Jenis Peminjam" value={detail.borrower_type_name} />
                  <Info label="Jumlah" value={formatRupiah(detail.amount)} />
                  <Info label="Tenor" value={`${detail.tenor_months ?? 0} bulan`} />
                  <Info label="Jasa %" value={detail.interest_rate ?? 0} />
                  <Info label="Total Pembayaran" value={formatRupiah(detail.total_payment)} />
                  <Info label="Angsuran/bulan" value={formatRupiah(detail.jumlah_angsuran)} />
                  <Info label="Sisa" value={formatRupiah(detail.sisa)} />
                  <Info label="Status" value={detail.status_label || detail.status} />
                </div>

                <div className="flex flex-wrap gap-2">
                  <LoadingButton
                    onClick={() => syncInstallment(detail.id)}
                    loading={busy === "sync"}
                    loadingText="Menyinkronkan…"
                    disabled={!(detail.installments || []).length}
                    data-testid="utang-sync-btn"
                    className="rounded-full bg-brand-soft text-brand px-5 py-2 text-sm hover:bg-brand/15"
                  >
                    <RefreshCcw size={14} /> Sinkronkan{firstUnpaid ? ` (Angs ${firstUnpaid.installment_number})` : ""}
                  </LoadingButton>
                  <button onClick={() => setEarlyOpen((v) => !v)} data-testid="utang-early-settle-btn" className="rounded-full bg-amber-100 text-amber-700 px-5 py-2 text-sm inline-flex items-center gap-2 hover:bg-amber-200 transition">
                    <Zap size={14} /> Pelunasan Dipercepat
                  </button>
                </div>

                {earlyOpen && (
                  <form onSubmit={submitEarlySettle} className="rounded-2xl border border-brand/10 bg-brand-soft/40 p-4 space-y-3" data-testid="utang-early-settle-form">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <FormField label="Tipe">
                        <select className="inp" value={earlyForm.type} onChange={(e) => setEarlyForm((p) => ({ ...p, type: e.target.value }))} data-testid="early-settle-type-select">
                          <option value="total">Total (lunasi)</option>
                          <option value="partial">Sebagian</option>
                        </select>
                      </FormField>
                      {earlyForm.type === "partial" && (
                        <FormField label="Jumlah Angsuran">
                          <input type="number" min="1" className="inp" value={earlyForm.count} onChange={(e) => setEarlyForm((p) => ({ ...p, count: e.target.value }))} data-testid="early-settle-count-input" />
                        </FormField>
                      )}
                      <FormField label="Tanggal Bayar">
                        <input type="date" required className="inp" value={earlyForm.payment_date} onChange={(e) => setEarlyForm((p) => ({ ...p, payment_date: e.target.value }))} data-testid="early-settle-date-input" />
                      </FormField>
                    </div>
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => setEarlyOpen(false)} disabled={earlySubmitting} className="rounded-full border border-brand/20 px-4 py-2 text-sm disabled:opacity-60">Batal</button>
                      <LoadingButton type="submit" loading={earlySubmitting} loadingText="Memproses…" data-testid="early-settle-submit-btn" className="rounded-full bg-amber-500 text-white px-5 py-2 text-sm hover:bg-amber-600">Proses</LoadingButton>
                    </div>
                    <style>{`.inp{width:100%;border-radius:12px;border:1px solid rgba(11,110,110,0.2);padding:9px 12px;font-size:14px;background:white;outline:none}.inp:focus{box-shadow:0 0 0 3px rgba(11,110,110,0.15);border-color:rgba(11,110,110,0.4)}`}</style>
                  </form>
                )}

                <div className="rounded-2xl border border-brand/10 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm whitespace-nowrap" data-testid="installments-table">
                      <thead className="bg-brand-soft/60 text-left text-brand-muted">
                        <tr>
                          <th className="px-4 py-3">Angs Ke</th>
                          <th className="px-4 py-3">Jatuh Tempo</th>
                          <th className="px-4 py-3 text-right">Pokok</th>
                          <th className="px-4 py-3 text-right">Jasa</th>
                          <th className="px-4 py-3 text-right">Total</th>
                          <th className="px-4 py-3">Status</th>
                          <th className="px-4 py-3">Tanggal Bayar</th>
                          <th className="px-4 py-3 text-right">Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detail.installments || []).length === 0 ? <tr><td colSpan={8} className="px-4 py-8 text-center text-brand-muted">Belum ada jadwal angsuran</td></tr>
                        : (detail.installments || []).map((inst) => (
                          <tr key={inst.id} className="border-t border-brand/10 hover:bg-brand-soft/30">
                            <td className="px-4 py-3">{inst.installment_number}</td>
                            <td className="px-4 py-3">{formatDate(inst.due_date)}</td>
                            <td className="px-4 py-3 text-right">{formatRupiah(inst.principal)}</td>
                            <td className="px-4 py-3 text-right">{formatRupiah(inst.interest)}</td>
                            <td className="px-4 py-3 text-right font-medium text-brand-ink">{formatRupiah(inst.total)}</td>
                            <td className="px-4 py-3">
                              <span className={"inline-flex items-center rounded-full text-xs px-2.5 py-1 " + (inst.status === "lunas" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700")}>
                                {inst.status}
                              </span>
                            </td>
                            <td className="px-4 py-3">{formatDate(inst.payment_date)}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-2">
                                {inst.status === "belum lunas" && (
                                  <LoadingButton
                                    onClick={() => payInstallment(inst, detail.id)}
                                    loading={busy === `pay-${inst.id}`}
                                    loadingText="Memproses…"
                                    data-testid={`pay-installment-${inst.id}`}
                                    className="rounded-full bg-emerald-500 text-white px-3 py-1.5 text-xs hover:bg-emerald-600"
                                  >
                                    Bayar
                                  </LoadingButton>
                                )}
                                {inst.proof_url && (
                                  <a href={inst.proof_url} target="_blank" rel="noreferrer" data-testid={`proof-${inst.id}`} className="rounded-full bg-brand-soft text-brand px-3 py-1.5 text-xs hover:bg-brand/15 transition">Bukti</a>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={"rounded-full px-3 py-1.5 text-xs " + (active ? "bg-brand text-white" : "bg-white border border-brand/15 text-brand hover:bg-brand-soft")}>
      {children}
    </button>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-brand-ink mb-1.5 uppercase tracking-[0.16em]">{label}</div>
      {children}
    </label>
  );
}

function Info({ label, value, className = "" }) {
  return (
    <div className={"rounded-xl bg-brand-soft/50 px-3 py-2 " + className}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted">{label}</div>
      <div className="text-sm font-medium text-brand-ink break-words">{value || "-"}</div>
    </div>
  );
}
